import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

/**
 * The query param that opens the survey. Deliberately opaque: the URL is
 * shared over email and chat, so it should not leak the feature name.
 */
export const WELLBEING_MEASURE_DEEP_LINK_PARAM = 'wm';

/** Only this exact value opts in, so a stray `?wm=1` cannot open the survey. */
const OPT_IN_VALUE = 'true';

/**
 * Reads the survey's deep-link param once, then rewrites the URL without it.
 *
 * The answer is captured on the first render and held, so the caller can act on
 * it whenever its data arrives rather than racing the rewrite. The param is
 * stripped whether or not it opted in — it has served its purpose either way,
 * and leaving it behind would reopen the survey on a refresh or a back
 * navigation.
 */
export const useWellbeingMeasureDeepLink = () => {
  const history = useHistory();
  const location = useLocation();
  const [isDeepLink] = useState(
    () =>
      new URLSearchParams(location.search).get(
        WELLBEING_MEASURE_DEEP_LINK_PARAM,
      ) === OPT_IN_VALUE,
  );
  const hasStripped = useRef(false);

  useEffect(() => {
    if (hasStripped.current) {
      return;
    }

    hasStripped.current = true;

    const params = new URLSearchParams(location.search);

    if (!params.has(WELLBEING_MEASURE_DEEP_LINK_PARAM)) {
      return;
    }

    params.delete(WELLBEING_MEASURE_DEEP_LINK_PARAM);

    const remaining = params.toString();

    history.replace(
      `${location.pathname}${remaining ? `?${remaining}` : ''}${location.hash}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isDeepLink;
};
